import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CircularProgress from "@mui/material/CircularProgress";

import { HrPerformanceCurveChart } from "./HrPerformanceCurveChart";
import { HrMetricIcon } from "./HrMetricIcon";
import { TrainingZonesInfoPanel } from "./TrainingZonesInfoPanel";
import { DurationMetricValue } from "./DurationMetricValue";
import {
  calculateMHR,
  calculateZoneDurations,
  calculateCalories,
  getZoneThresholds,
  intensityPercent,
} from "../features/trainingSummary/zoneUtils";
import { extractHrStats } from "../utils/hrSeries";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import "./PlayerSessionResultsDialog.css";

function formatZoneDuration(sec) {
  const s = Math.round(sec);
  if (s <= 0) return "0s";
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m > 0) {
    return r > 0 ? `${m}m ${r}s` : `${m}m`;
  }
  return `${r}s`;
}

function HrMetricWidget({ label, value, unit, footnote, variant }) {
  return (
    <div
      className={`player-exercise-details__hr-subcard player-exercise-details__hr-subcard--${variant}`}
    >
      <div className="player-exercise-details__hr-subcard-header">
        <HrMetricIcon
          variant={variant}
          size={14}
          className={`player-exercise-details__hr-subcard-icon player-exercise-details__hr-subcard-icon--${variant}`}
        />
        <span>{label}</span>
      </div>
      <div className="player-exercise-details__hr-subcard-value">
        {value}
        {unit ? (
          <>
            {" "}
            <span className="player-exercise-details__hr-subcard-unit">{unit}</span>
          </>
        ) : null}
      </div>
      {footnote ? (
        <span className="session-summary-dialog__metric-footnote">{footnote}</span>
      ) : null}
    </div>
  );
}

function ZoneRow({ badge, badgeClass, name, durationSec, progressPct, barClass }) {
  return (
    <div className="player-exercise-details__zone-row">
      <div className="player-exercise-details__zone-header">
        <div className={`player-exercise-details__zone-badge ${badgeClass}`}>
          {badge}
        </div>
        <div className="player-exercise-details__zone-info">
          <span className="player-exercise-details__zone-name">{name}</span>
        </div>
        <div className="player-exercise-details__zone-duration">
          {formatZoneDuration(durationSec)}
        </div>
      </div>
      <div className="player-exercise-details__zone-progress-wrap">
        <div
          className={`player-exercise-details__zone-progress-bar ${barClass}`}
          style={{ width: `${progressPct}%` }}
        />
      </div>
    </div>
  );
}

export function SessionSummaryDialog({
  open,
  onClose,
  onBack,
  sessionNumber = null,
  playerName = null,
  hrInfo = null,
  physiology = { age: 30, gender: "male", weightKg: 70 },
  loading = false,
}) {
  const { t } = useTranslation();
  useBodyScrollLock(open);

  const stats = useMemo(() => extractHrStats(hrInfo), [hrInfo]);
  const mhr = useMemo(
    () => calculateMHR({ age: physiology.age, gender: physiology.gender }),
    [physiology.age, physiology.gender]
  );

  const maxHr = stats.maxHr;
  const avgHr = stats.avgHr;
  const maxIntensity = intensityPercent(maxHr, mhr);
  const avgIntensity = intensityPercent(avgHr, mhr);
  const totalDurationSec = stats.summaryDurationSec || 0;
  const hasDuration = totalDurationSec > 0;

  const zoneDurations = useMemo(
    () =>
      calculateZoneDurations({
        mhr,
        hrPoints: stats.hrPoints,
        totalDurationSec:
          totalDurationSec > 0 ? totalDurationSec : stats.hrPoints.length,
      }),
    [mhr, stats.hrPoints, totalDurationSec]
  );

  const calories = useMemo(
    () =>
      calculateCalories({
        zoneDurations,
        weightKg: physiology.weightKg ?? 70,
      }),
    [zoneDurations, physiology.weightKg]
  );

  const zoneThresholds = useMemo(() => getZoneThresholds(mhr), [mhr]);
  const totalZoneSec = useMemo(
    () => Object.values(zoneDurations).reduce((sum, val) => sum + val, 0),
    [zoneDurations]
  );

  const hasHrData = maxHr > 0 || avgHr > 0 || stats.hrPoints.length > 0;

  const title =
    sessionNumber != null
      ? `${t("sessionSingular", "Session")} #${sessionNumber}`
      : t("sessionDetails", "Session Details");

  const subtitle = playerName
    ? t("session_summary_subtitle", "Session Summary · {{name}}", { name: playerName })
    : t("session_summary_subtitle_self", "Session Summary");

  const zonePct = (zone) =>
    totalZoneSec > 0 ? (zoneDurations[zone] / totalZoneSec) * 100 : 0;

  if (!open) return null;

  return createPortal(
    <div
      className="player-session-results__backdrop player-session-results__backdrop--nested"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="player-session-results__sheet player-session-results__sheet--nested"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="player-session-results__handle" />

        <button
          type="button"
          className="player-session-results__close"
          onClick={onClose}
          aria-label={t("close", "Close")}
        >
          <CloseIcon />
        </button>

        <div className="player-session-results__content">
          <div className="player-session-results__header">
            <button
              type="button"
              className="player-session-results__back-btn"
              onClick={onBack || onClose}
            >
              <ArrowBackIcon fontSize="small" />
              <span>{t("back", "Back")}</span>
            </button>
            <div className="player-session-results__header-new">
              <div className="player-session-results__header-text">
                <h2 className="player-session-results__title-new">{title}</h2>
                <p className="player-session-results__subtitle-new">{subtitle}</p>
              </div>
            </div>
          </div>

          <div className="player-session-results__body">
            {loading ? (
              <div className="player-session-results__loading">
                <CircularProgress size={32} style={{ color: "#0877bc" }} />
              </div>
            ) : !hasHrData ? (
              <div className="player-session-results__empty player-session-results__empty--hr player-session-results__empty-box">
                <div className="session-summary-dialog__empty-icon-wrap">
                  <FavoriteIcon style={{ color: "#FF453A", fontSize: 32 }} />
                </div>
                <h4 className="session-summary-dialog__empty-title">
                  {t("noHrDataRecorded", "No heart rate data recorded")}
                </h4>
                <p className="session-summary-dialog__empty-text">
                  {t(
                    "session_summary_no_hr_description",
                    "There are no heart rate recordings found for this player in this session."
                  )}
                </p>
              </div>
            ) : (
              <div className="player-exercise-details__hr-section">
                <h3 className="player-exercise-details__section-title">
                  {t("hr_training_summary", "Heart Rate Training Summary")}
                </h3>

                <div className="player-exercise-details__hr-dashboard">
                  <div className="player-exercise-details__hr-dashboard-metrics">
                    <div className="player-exercise-details__hr-summary-card player-exercise-details__hr-summary-card--session">
                      <HrMetricWidget
                        label={t("session_duration", "Duration")}
                        value={
                          hasDuration ? (
                            <DurationMetricValue totalSec={totalDurationSec} />
                          ) : (
                            "—"
                          )
                        }
                        unit=""
                        variant="duration"
                      />
                      <HrMetricWidget
                        label={t("calories", "Calories")}
                        value={calories > 0 ? calories : "—"}
                        unit={calories > 0 ? "kcal" : ""}
                        variant="calories"
                      />
                      <HrMetricWidget
                        label={t("max_hr", "Max HR")}
                        value={maxHr > 0 ? Math.round(maxHr) : "—"}
                        unit={maxHr > 0 ? "bpm" : ""}
                        variant="max"
                      />
                      <HrMetricWidget
                        label={t("max_intensity", "Max Intensity")}
                        value={maxIntensity > 0 ? maxIntensity : "—"}
                        unit={maxIntensity > 0 ? "%" : ""}
                        variant="max_intensity"
                      />
                      <HrMetricWidget
                        label={t("avg_hr", "Avg HR")}
                        value={avgHr > 0 ? Math.round(avgHr) : "—"}
                        unit={avgHr > 0 ? "bpm" : ""}
                        variant="avg"
                      />
                      <HrMetricWidget
                        label={t("avg_intensity", "Avg Intensity")}
                        value={avgIntensity > 0 ? avgIntensity : "—"}
                        unit={avgIntensity > 0 ? "%" : ""}
                        variant="avg_intensity"
                      />
                    </div>
                  </div>

                  <div className="player-exercise-details__hr-dashboard-chart">
                    <HrPerformanceCurveChart
                      hrInfo={hrInfo}
                      mhr={mhr}
                    />
                  </div>

                  <div className="player-exercise-details__hr-dashboard-zones">
                    <div className="player-exercise-details__hr-zones-card">
                  <TrainingZonesInfoPanel
                    mhr={mhr}
                    age={physiology?.age}
                    gender={physiology?.gender}
                  />
                  <p className="session-summary-dialog__mhr-caption">
                    {t("session_training_summary_mhr_of", "of {{mhr}} bpm MHR", {
                      mhr,
                    })}
                  </p>

                  <div className="player-exercise-details__zone-list">
                    <ZoneRow
                      badge="Z5"
                      badgeClass="player-exercise-details__zone-badge--z5"
                      name={t("zone_peak_bpm", "Peak ({{min}}–{{max}} bpm)", {
                        min: zoneThresholds[5][0],
                        max: zoneThresholds[5][1],
                      })}
                      durationSec={zoneDurations[5]}
                      progressPct={zonePct(5)}
                      barClass="player-exercise-details__zone-progress-bar--z5"
                    />
                    <ZoneRow
                      badge="Z4"
                      badgeClass="player-exercise-details__zone-badge--z4"
                      name={t("zone_cardio_bpm", "Cardio ({{min}}–{{max}} bpm)", {
                        min: zoneThresholds[4][0],
                        max: zoneThresholds[4][1],
                      })}
                      durationSec={zoneDurations[4]}
                      progressPct={zonePct(4)}
                      barClass="player-exercise-details__zone-progress-bar--z4"
                    />
                    <ZoneRow
                      badge="Z3"
                      badgeClass="player-exercise-details__zone-badge--z3"
                      name={t("zone_aerobic_bpm", "Aerobic ({{min}}–{{max}} bpm)", {
                        min: zoneThresholds[3][0],
                        max: zoneThresholds[3][1],
                      })}
                      durationSec={zoneDurations[3]}
                      progressPct={zonePct(3)}
                      barClass="player-exercise-details__zone-progress-bar--z3"
                    />
                    <ZoneRow
                      badge="Z2"
                      badgeClass="player-exercise-details__zone-badge--z2"
                      name={t("zone_fatburn_bpm", "Fat Burn ({{min}}–{{max}} bpm)", {
                        min: zoneThresholds[2][0],
                        max: zoneThresholds[2][1],
                      })}
                      durationSec={zoneDurations[2]}
                      progressPct={zonePct(2)}
                      barClass="player-exercise-details__zone-progress-bar--z2"
                    />
                    <ZoneRow
                      badge="Z0/Z1"
                      badgeClass="player-exercise-details__zone-badge--z01"
                      name={t("zone_warmup_inactive_bpm", "Warm Up / Inactive (<{{min}} bpm)", {
                        min: zoneThresholds[2][0],
                      })}
                      durationSec={zoneDurations[1] + zoneDurations[0]}
                      progressPct={zonePct(1) + zonePct(0)}
                      barClass="player-exercise-details__zone-progress-bar--z01"
                    />
                  </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default SessionSummaryDialog;
